<template>
  <v-card class="mx-auto result-card" max-width="380" elevation="4" color="tertiary">
    <!-- Image produit avec overlay gradient -->
    <div class="image-container">
      <v-img
        height="150"
        :src="product.productImage || 'https://via.placeholder.com/220'"
        cover
        class="product-image"
      >
        <div class="image-overlay"></div>
      </v-img>
    </div>

    <!-- Header avec nom et marque -->
    <v-card-title class="product-title pb-1">
      {{ product.productName }}
    </v-card-title>
    <v-card-subtitle class="product-brand text-grey pt-0">
      {{ product.productBrand }}
    </v-card-subtitle>

    <v-divider class="mx-4 mb-3 divider-soft"></v-divider>

    <!-- Alerte intolérances détectées -->
    <div class="px-4 pb-2" v-if="unsafeIntolerances.length">
      <div class="warning-banner">
        <v-icon size="small" class="mr-2">mdi-alert-circle</v-icon>
        <span>
          Produit incompatible avec vos intolérances :
          <strong>{{ unsafeIntolerancesLabel }}</strong>
        </span>
      </div>
    </div>

    <!-- Alerte allergènes incompatibles -->
    <div class="px-4 pb-2" v-if="unsafeAllergens.length">
      <div class="warning-banner allergen-warning">
        <v-icon size="small" class="mr-2">mdi-alert</v-icon>
        <span>
          Allergènes incompatibles avec votre profil :
          <strong>{{ unsafeAllergensLabel }}</strong>
        </span>
      </div>
    </div>

    <!-- Section Nova Group et Nutri-Score -->
    <div class="px-4 pb-2">
      <div class="meta-row">
        <v-chip
          v-if="product.novaGroup?.string"
          :color="product.novaGroup.color"
          class="mr-2 mb-2 chip-nova"
          size="small"
          variant="tonal"
        >
          <span class="font-weight-medium">{{ $t(product.novaGroup.string) }}</span>
        </v-chip>
        <div class="nutri-score-wrapper mb-2">
          <div :class="['nutri-score-pill', nutriScoreClass]">
            <span class="nutri-score-letter">{{ nutriScoreLetter || "—" }}</span>
          </div>
          <span class="nutri-score-label">Nutri-Score</span>
        </div>
      </div>
    </div>

    <!-- Section Nutriments -->
    <div class="px-4 pb-3" v-if="product.nutriments_map?.length">
      <h4 class="section-title text-subtitle-2 font-weight-bold mb-2 text-grey">
        Informations nutritionnelles
      </h4>
      <div class="nutriments-list">
        <div
          v-for="(nutrient, i) in product.nutriments_map.slice(0, 4)"
          :key="'nut-' + i"
          class="nutriment-row"
        >
          <span class="nutriment-key">{{ $t(nutrient.key) }}</span>
          <span class="nutriment-value">{{ nutrient.value }}</span>
        </div>
      </div>
    </div>
    <div v-else class="px-4 pb-3">
      <p class="text-body-2 text-grey-darken-1 mb-0">
        Aucune information nutritionnelle disponible
      </p>
    </div>

    <!-- Section Allergènes -->
    <div class="px-4 pb-3">
      <h4 class="section-title text-subtitle-2 font-weight-bold mb-2 text-grey">
        Allergènes
      </h4>
      <div v-if="product.allergens?.length" class="allergens-banner">
        <v-icon size="small" class="mr-2">mdi-alert</v-icon>
        <span>{{ product.allergens.map((a) => $t(a)).join(", ") }}</span>
      </div>
      <div v-else class="pb-3">
        <p class="text-body-2 text-success font-weight-medium mb-0">
          ✓ Aucun allergène connu
        </p>
      </div>
    </div>

    <!-- Section Additifs -->
    <!-- <div class="px-4 pb-3">
      <h4 class="text-subtitle-2 font-weight-bold mb-2 text-grey">
        Additifs
      </h4>
      <div class="additives-scroll" v-if="product.additives?.length">
        <v-chip
          v-for="(a, i) in product.additives"
          :key="'ad-' + i"
          color="red-darken-2"
          class="mr-2 mb-2"
          size="small"
          variant="tonal"
        >
          {{ a }}
        </v-chip>
      </div>
      <div v-else class="pb-3">
        <p class="text-body-2 text-success font-weight-medium mb-0">
          ✓ Aucun additif connu
        </p>
      </div>
    </div> -->

    <!-- Section Portion -->
    <div class="px-4 pb-3" v-if="product.servingQuantity || product.servingSize">
      <div class="portion-info">
        <v-icon size="small" class="mr-1">mdi-scale-balance</v-icon>
        <span class="text-body-2 portion-text">
          <span v-if="product.servingQuantity">Portion: {{ product.servingQuantity }}g</span>
          <span v-if="product.servingQuantity && product.servingSize"> • </span>
          <span v-if="product.servingSize">{{ product.servingSize }}</span>
        </span>
      </div>
    </div>

    <v-divider class="mx-4 mt-2 divider-soft"></v-divider>

    <!-- Actions -->
    <v-card-actions class="px-4 pt-3 pb-4">
      <v-btn
        :color="alreadyAdded ? 'success' : 'accent'"
        :disabled="alreadyAdded"
        @click="$emit('add', product)"
        variant="flat"
        block
        size="large"
        class="action-button"
      >
        <v-icon v-if="alreadyAdded" class="mr-2">mdi-check-circle</v-icon>
        {{ alreadyAdded ? "Produit ajouté" : "Ajouter l'ingrédient" }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup>
import { defineProps, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/store/auth_store";
import { checkProductWithIntolerances } from "@/service/intolerance_service";

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  alreadyAdded: {
    type: Boolean,
    default: false,
  },
});

const authStore = useAuthStore();
const { t } = useI18n();

const normalizeKey = (value) =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const unsafeIntolerances = computed(() => {
  const userIntolerances = [
    ...(Array.isArray(authStore.userInfos?.intolerances)
      ? authStore.userInfos.intolerances
      : []),
    ...(Array.isArray(authStore.userInfos?.allergies)
      ? authStore.userInfos.allergies
      : []),
  ];
  const productInfo = props.product?.foodInfo;
  if (!userIntolerances.length || !productInfo) return [];
  return checkProductWithIntolerances(productInfo, userIntolerances);
});

const unsafeIntolerancesLabel = computed(() =>
  unsafeIntolerances.value.map((i) => `"${t(i)}"`).join(", ")
);

const unsafeAllergens = computed(() => {
  const userAllergies = authStore.userInfos?.allergies || [];
  const productAllergens = props.product?.allergens || [];
  if (!userAllergies.length || !productAllergens.length) return [];
  const userSet = new Set(userAllergies.map((a) => normalizeKey(a)));
  return productAllergens.filter((a) => userSet.has(normalizeKey(a)));
});

const unsafeAllergensLabel = computed(() =>
  unsafeAllergens.value.map((a) => `"${t(a)}"`).join(", ")
);

const nutriScoreLetter = computed(() => {
  const raw = props.product?.nutriscore;
  if (!raw) return "";
  return String(raw).trim().toUpperCase();
});

const nutriScoreClass = computed(() => {
  const letter = nutriScoreLetter.value;
  const allowed = ["A", "B", "C", "D", "E"];
  if (allowed.includes(letter)) {
    return `nutri-score-${letter.toLowerCase()}`;
  }
  return "nutri-score-unknown";
});
</script>

<style scoped>
.result-card {
  flex: 0 0 var(--card-width, 320px);
  width: var(--card-width, 320px);
  max-width: 100%;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-secondary));
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.22);
  color: rgb(var(--v-theme-text));
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

.result-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
}

.image-container {
  position: relative;
  overflow: hidden;
  border-radius: 16px 16px 12px 12px;
}

.product-image {
  transition: transform 0.3s ease;
}

.result-card:hover .product-image {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(
    to top,
    rgba(var(--v-theme-primary), 0.75),
    rgba(var(--v-theme-primary), 0)
  );
}

.product-title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
  color: rgb(var(--v-theme-text));
  padding-top: 12px;
}

.product-brand {
  font-size: 0.82rem;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.65);
}

.nutriments-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.nutriment-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 6px 8px;
  background: rgba(var(--v-theme-text), 0.06);
  border: 1px solid rgba(var(--v-theme-text), 0.08);
  border-radius: 8px;
}

.nutriment-key {
  color: rgba(var(--v-theme-text), 0.65);
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.nutriment-value {
  font-weight: 600;
  color: rgb(var(--v-theme-accent));
  font-size: 0.82rem;
}

.allergens-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(var(--v-theme-tertiary), 0.45);
  border: 1px solid rgba(var(--v-theme-text), 0.18);
  color: rgb(var(--v-theme-text));
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.2;
}

.allergens-banner .v-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  position: relative;
  top: -1px;
}

.portion-info {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  background: rgba(var(--v-theme-text), 0.06);
  border-radius: 10px;
  border: 1px solid rgba(var(--v-theme-text), 0.08);
}

.portion-text {
  color: rgb(var(--v-theme-text));
}

.action-button {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.25px;
  border-radius: 14px;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);
}

/* Amélioration du contraste des chips */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.25px;
}

.section-title {
  color: rgba(var(--v-theme-text), 0.6);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.72rem;
}

.divider-soft {
  opacity: 0.12;
}

.chip-nova {
  border: 1px solid rgba(var(--v-theme-text), 0.18);
}

.meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.nutri-score-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.nutri-score-pill {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.82rem;
  color: rgb(var(--v-theme-text));
  border: 1px solid rgba(var(--v-theme-text), 0.2);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.2);
}

.nutri-score-letter {
  line-height: 1;
  text-transform: uppercase;
}

.nutri-score-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(var(--v-theme-text), 0.65);
}

.nutri-score-a {
  background: rgb(var(--v-theme-accent));
}

.nutri-score-b {
  background: rgba(var(--v-theme-accent), 0.75);
}

.nutri-score-c {
  background: rgba(var(--v-theme-tertiary), 0.9);
}

.nutri-score-d {
  background: rgba(var(--v-theme-error), 0.75);
}

.nutri-score-e {
  background: rgb(var(--v-theme-error));
}

.nutri-score-unknown {
  background: rgba(var(--v-theme-text), 0.18);
}

.chip-nutriment {
  background: rgba(var(--v-theme-accent), 0.16) !important;
  border: 1px solid rgba(var(--v-theme-accent), 0.35);
}

.chip-allergen {
  background: rgba(var(--v-theme-error), 0.16) !important;
  border: 1px solid rgba(var(--v-theme-error), 0.35);
}

.warning-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(var(--v-theme-error), 0.16);
  border: 1px solid rgba(var(--v-theme-error), 0.45);
  color: rgb(var(--v-theme-text));
  font-size: 0.82rem;
  line-height: 1.4;
}

.allergen-warning {
  background: rgba(var(--v-theme-error), 0.2);
  border-color: rgba(var(--v-theme-error), 0.6);
}

/* Scrollbar personnalisée pour un look plus pro */
.additives-scroll::-webkit-scrollbar {
  height: 6px;
}

.additives-scroll::-webkit-scrollbar-track {
  background: rgba(var(--v-theme-primary), 0.8);
  border-radius: 10px;
}

.additives-scroll::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-text), 0.25);
  border-radius: 10px;
}

.additives-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-text), 0.4);
}

/* Responsive */
@media (max-width: 600px) {
  .result-card {
    width: min(86vw, 320px);
    flex-basis: min(86vw, 320px);
  }
  .product-title {
    font-size: 1rem;
  }
}
</style>
